import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import type { SearchableOption } from './SearchableSelect';

/** Rows rendered at once. Past this the list asks for a search instead of drawing thousands. */
const MAX_SHOWN = 200;

/**
 * Multi-select with type-to-filter — SearchableSelect's sibling for picking several values
 * (trades, cities, a list of stores).
 *
 * Same panel mechanics as SearchableSelect: a FIXED-position portal to <body>, repositioned
 * on scroll and resize, flipped above the control when there's no room below, and z-index
 * 140 so it clears the shared Modal (see the ladder noted there). It differs only in what a
 * pick does: a row toggles in place and the panel stays open, so several values can be
 * chosen in one visit.
 *
 * Long lists (a project's ~2,500 stores) draw at most MAX_SHOWN rows; typing narrows them.
 */
export default function MultiSearchableSelect({
  id,
  values,
  onChange,
  options,
  placeholder = 'Any',
  searchPlaceholder = 'Type to search…',
  disabled = false,
  emptyText = 'No matches',
}: {
  id?: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: SearchableOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  emptyText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);

  const selected = useMemo(() => new Set(values), [values]);
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? options.filter((o) => o.label.toLowerCase().includes(t)) : options;
  }, [options, q]);
  const shown = matches.slice(0, MAX_SHOWN);

  // Name up to two picks on the control; past that, a count reads better than a truncation.
  const summary = useMemo(() => {
    if (values.length === 0) return '';
    const labels = values.map((v) => options.find((o) => o.value === v)?.label ?? v);
    return labels.length <= 2 ? labels.join(', ') : `${labels.length} selected`;
  }, [values, options]);

  useEffect(() => {
    if (!open) { setQ(''); setActive(0); }
  }, [open]);
  useEffect(() => { setActive(0); }, [q]);

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const H = 340; // approx panel height, incl. the search box and the action row
      const below = window.innerHeight - r.bottom;
      const openUp = below < H && r.top > below;
      setPos({
        left: r.left,
        width: Math.max(r.width, 240),
        ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    // Guarded: jsdom has no scrollIntoView.
    if (typeof el?.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const toggle = (value: string) => {
    onChange(selected.has(value) ? values.filter((v) => v !== value) : [...values, value]);
  };

  // "Select shown" adds what the search currently shows, so "type 'Kerala', select shown"
  // picks every Kerala city without touching earlier picks.
  const selectShown = () => {
    const next = [...values];
    for (const o of matches) if (!selected.has(o.value)) next.push(o.value);
    onChange(next);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = shown[active];
      if (hit) toggle(hit.value);
    }
  };

  return (
    <>
      <button
        id={id}
        ref={btnRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        className={`flex w-full items-center justify-between rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 ${
          disabled ? 'cursor-not-allowed bg-gray-50 text-gray-400' : 'bg-white'
        }`}
      >
        <span className={`truncate text-left ${summary ? 'text-gray-900' : 'text-gray-500'}`}>
          {summary || placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 140 }}
          className="rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <div className="border-b border-gray-100 p-2">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder={searchPlaceholder}
              autoFocus
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <div className="mt-1.5 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={selectShown}
                disabled={matches.length === 0}
                className="font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-300"
              >
                Select {q.trim() ? 'shown' : 'all'}
              </button>
              <button
                type="button"
                onClick={() => onChange([])}
                disabled={values.length === 0}
                className="font-medium text-gray-500 hover:text-gray-700 disabled:text-gray-300"
              >
                Clear
              </button>
            </div>
          </div>
          <div ref={listRef} role="listbox" aria-multiselectable="true" className="max-h-60 overflow-y-auto py-1">
            {shown.map((o, i) => {
              const on = selected.has(o.value);
              return (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={on}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => toggle(o.value)}
                  className={`flex cursor-pointer items-center justify-between px-3 py-1.5 text-sm ${
                    on ? 'bg-primary-50 text-primary-700' : i === active ? 'bg-gray-50 text-gray-700' : 'text-gray-700'
                  }`}
                >
                  <span className="truncate">{o.label}</span>
                  {on && <Check className="h-4 w-4 flex-shrink-0 text-primary-600" />}
                </div>
              );
            })}
            {matches.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">{emptyText}</div>}
          </div>
          {matches.length > MAX_SHOWN && (
            <div className="border-t border-gray-100 px-3 py-1.5 text-xs text-gray-500">
              Showing {MAX_SHOWN} of {matches.length.toLocaleString()} — type to narrow down.
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
